import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal } from '../../../../component/Modal'
import { DuetButtons } from '../../../../component/ActionButtons/DuetButtons'
import { SimpleButton } from '../../../../component/Form/Button/SimpleButton'
import { Food, IngredientFood } from '../../../../core/db_food'
import { importFoodsFromDietFile, parseNumber, readCsvFileToUtf8, splitCsvLine } from './coreFoodsImport'
import { FromInput } from '../../FoodEditPage/SimpleFood'
import { Select } from '../../../../component/Form/MealtimeDateTime/SelectMealtimeType'
import { curTime } from '../../../../core/helper'
import { changeCustomTag } from './core'

// const ACCEPT_DIET = '.dietgz, application/json'

export const ImportFoodsModal: React.FC<{
    isShow: boolean
    onClose: () => void
    onImport: (foods: Food[]) => void
    onSuccess?: () => void
}> = (props) => {
    const [linesAndColumns, setLinesAndColumns] = useState<string[][]>([])
    const onImport = useCallback((foods: Food[]) => {
        props.onImport(foods)
        props.onSuccess?.()
        props.onClose()
    }, [props.onImport])
    const onCsvImport = (linesAndColumns: string[][]) => {
        setLinesAndColumns(linesAndColumns)
    }
    useEffect(() => {
        if (props.isShow) {
            setLinesAndColumns([])
        }
    }, [props.isShow])

    const changed = useCallback((visible: boolean) => {
        if (!visible) {
            props.onClose()
        }
    }, [props.onClose])

    if (!props.isShow) {
        return null
    }

    return (
        <div style={{
            position: 'fixed', zIndex: 1000,
            top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }}>
            <Modal isShow={props.isShow} changed={changed}>
                {
                    linesAndColumns.length > 0 ? (
                        <CsvImportView linesAndColumns={linesAndColumns} onImport={onImport} onCancel={() => setLinesAndColumns([])} />
                    ) : (
                        <BaseView onCsvImport={onCsvImport} onImport={onImport} onCancel={props.onClose} />
                    )
                }
            </Modal>
        </div>
    )
}


/** Оценка высоты строки превью (padding + line-height + нижняя граница + grid gap) для `top` у многострочного sticky-заголовка. */
const CSV_PREVIEW_HEADER_ROW_PX = 24
const COLORS = {
    bgError: '#ffe4e6',
    bgHeader: '#f2f2f2',
    warning: '#fff3cd',
    success: '#d4edda',
}
type OptionType = {
    typeId: number
    text: string
}
type OptionTypeWithPossibleValues = OptionType & {
    possibleValues: string[]
    attribute: string
}
const Columns: OptionTypeWithPossibleValues[] = [
    { typeId: 0, text: 'Не определено', possibleValues: [], attribute: 'none' },
    { typeId: 1, text: 'Название', attribute: 'name', possibleValues: ['название', 'продукт', 'продукты'] },
    { typeId: 2, text: 'ФА', attribute: 'fa', possibleValues: ['фа'] },
    { typeId: 3, text: 'Белки', attribute: 'p', possibleValues: ['белки', 'белок'] },
    { typeId: 4, text: 'Жиры', attribute: 'f', possibleValues: ['жиры', 'жир'] },
    { typeId: 5, text: 'Углеводы', attribute: 'c', possibleValues: ['углеводы', 'углевод'] },
    { typeId: 6, text: 'Ккал', attribute: 'e', possibleValues: ['ккал', 'калории', 'энергия'] },
    { typeId: 7, text: 'Теги', attribute: 'tags', possibleValues: ['теги', 'тег', 'tags'] },
]
const IdToColumns = new Map<number, OptionTypeWithPossibleValues>(Columns.map(one => [one.typeId, one]))
const CsvImportView: React.FC<{
    linesAndColumns: string[][]
    onImport: (foods: Food[]) => void
    onCancel: () => void
}> = ({ linesAndColumns, onImport, onCancel }) => {
    const [countHeaderLine, setCountHeaderLine] = useState(0)
    const [foods, setFoods] = useState<Food[]>([])
    const [wrongLines, setWrongLines] = useState<number[]>([])
    const [showMode, setShowMode] = useState<'all' | 'wrong'>('all')
    const [columns, setColumns] = useState<{ index: number, content: string[], typeId: number }[]>([])
    const detectedColumns = useMemo(() => {
        return detectColumnType(linesAndColumns.slice(0, countHeaderLine), Columns)
    }, [linesAndColumns, countHeaderLine])
    useEffect(() => {
        setColumns(linesAndColumns[0].map((column, index) => {
            const detectedColumn = detectedColumns.find(one => one.columnIndex === index)
            return {
                index,
                content: [],
                // content: linesAndColumns.map((line) => line[index]),
                typeId: detectedColumn?.typeId ?? 0,
            }
        }))
    }, [linesAndColumns, detectedColumns])
    const changeColumnType = useCallback((index: number, item: OptionType) => {
        setColumns(columns.map((column) => {
            if (column.index === index) {
                return { ...column, typeId: item.typeId }
            }
            if (column.typeId == item.typeId) {
                return { ...column, typeId: 0 }
            }
            return column
        }))
    }, [columns])
    const columnCount = useMemo(
        () => linesAndColumns.reduce((max, line) => Math.max(max, line.length), 0),
        [linesAndColumns],
    )
    const minNeedColumns = useMemo(() => {
        return columns.reduce((sum: Set<number>, curr: { typeId: number })=>{
            sum.delete(curr.typeId)
            return sum
        }, new Set<number>([1, 2, 3, 4, 5, 6]))
        // return columns.every(column => column.typeId !== 0)
    }, [columns])

    useEffect(() => {
        if (minNeedColumns.size > 0) {
            setFoods([])
            setWrongLines([])
            setShowMode('all')
            return
        }
        const results = linesAndColumns.slice(countHeaderLine).map((line, index) => {
            try {
                let food: Food = columns.reduce((acc, curr) => {
                    let txt: string | number = line[curr.index]
                    if (curr.typeId >= 2 && curr.typeId <= 6) {
                        if (txt.trim() == '') {
                            // eslint-disable-next-line max-len
                            const msg = `строка: ${line.join('; ')} значение «${txt}» не может быть пустым для колонки: ${IdToColumns.get(curr.typeId)!.text}`
                            throw new Error(msg)
                        }
                        // try to parse number
                        try {
                            txt = parseNumber(txt)
                        } catch (err: unknown) {
                            // eslint-disable-next-line max-len
                            const msg = `строка: ${line.join('; ')} значение «${txt}» не может быть преобразовано в число для колонки: ${IdToColumns.get(curr.typeId)!.text}`
                            throw new Error(msg)
                        }

                    }
                    return {
                        ...acc,
                        [IdToColumns.get(curr.typeId)!.attribute as keyof Food]: txt,
                    }
                }, {
                    dt: index + 1,
                    m: false,
                    mc: [] as IngredientFood[],
                    mcw: 100,
                    lw: 100,
                    lu: curTime(true),
                } as Food)
                if ('tags' in food && food.tags && typeof food.tags === 'string' && food.tags.trim().length > 0) {
                    const tags = food.tags.split(';').map(tag => tag.trim()).join(';')
                    food = changeCustomTag('save', tags, [food])[0]
                    delete (food as any).tags
                }
                return food
            } catch (err: unknown) {
                // console.error('error', err)
                return index
            }

        })

        const wrongLines = results.filter(one => typeof one === 'number') as number[]
        setWrongLines(wrongLines)
        setFoods(results.filter(one => typeof one !== 'number') as Food[])
        return
    }, [linesAndColumns, minNeedColumns])
    const runImport = useCallback(() => {
        onImport(foods)
    }, [foods, onImport])
    return (
        <>
            <div style={{ padding: '2px 0 8px 0', fontSize: 14 }}>
                <h1>Настройки импорта</h1>
                <div style={{ paddingBottom: 4 }}>
                    {/* <SmartCheckbox
                        title='Есть заголовок'
                        value={countHeaderLine > 0}
                        change={() => countHeaderLine > 0 ? setCountHeaderLine(0) : setCountHeaderLine(1)}
                    /> */}
                    {/* <div> */}
                    <FromInput label='Количество строк заголовка:' labelExt=''
                        value={countHeaderLine} step={1}
                        change={(value) => setCountHeaderLine(value)}
                        disabled={false}
                    />
                    {/* </div> */}
                </div>
                <div
                    style={{
                        maxHeight: 220,
                        overflow: 'auto',
                        display: 'grid',
                        gridTemplateColumns: `repeat(${Math.max(columnCount, 1)}, minmax(72px, 1fr))`,
                        gap: 1,
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        borderRadius: 4,
                        fontSize: 12,
                        lineHeight: 1.3,
                    }}
                >
                    {
                        columns.map((column, index) => {
                            return (
                                <div key={column.index + 'select'} style={{
                                    padding: '0px 6px',
                                    // center text
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: `${CSV_PREVIEW_HEADER_ROW_PX}px`,
                                    backgroundColor: minNeedColumns.size === 0 ? COLORS.bgHeader : COLORS.bgError,
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    position: 'sticky',
                                    top: 0 * CSV_PREVIEW_HEADER_ROW_PX,
                                    zIndex: countHeaderLine - 0,

                                }}>
                                    <Select key={column.index + 'select'}
                                        options={Columns} change={(v) => changeColumnType(index, v)} typeId={column.typeId} disable={false}
                                        style={{ fontSize: 12,
                                            backgroundColor: column.typeId === 0
                                                ? '#ffe4e6' // светло-красный/розовый - очень светлый оттенок красного/розового
                                                : '#fff' }} />
                                </div>
                            )
                        })
                    }
                    {linesAndColumns.map((line, rowIndex) =>
                        Array.from({ length: columnCount }, (_, colIndex) => {
                            const isHeaderRow = countHeaderLine > 0 && rowIndex < countHeaderLine
                            const isWrongLine = wrongLines.includes(rowIndex - countHeaderLine)
                            if (showMode == 'wrong' && !isWrongLine && !isHeaderRow) {
                                return null
                            }
                            return (
                                <div
                                    key={`${rowIndex}-${colIndex}`}
                                    style={{
                                        padding: '0px 6px',
                                        // center text
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: `${CSV_PREVIEW_HEADER_ROW_PX}px`,
                                        backgroundColor: isHeaderRow
                                            ? COLORS.bgHeader
                                            : isWrongLine ? COLORS.bgError : '#fff',
                                        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        ...(isHeaderRow
                                            ? {
                                                position: 'sticky',
                                                top: rowIndex * CSV_PREVIEW_HEADER_ROW_PX,
                                                zIndex: countHeaderLine - rowIndex,
                                            }
                                            : {}),
                                    }}
                                    title={line[colIndex] ?? ''}
                                >
                                    {line[colIndex] ?? ''}
                                </div>
                            )
                        }),
                    )}
                </div>
                <div>
                    {
                        minNeedColumns.size > 0 && <div style={{ color: 'red', fontSize: 12 }}>
                        Не хватает колонок: {Array.from(minNeedColumns).map((column) => (
                                <span key={column} style={{ marginRight: 4 }}>{Columns.find(one => one.typeId === column)?.text}</span>
                            ))}
                        </div>
                    }
                    {
                        foods.length > 0 && <div style={{ color: 'green', fontSize: 12, paddingTop: 4 }}>
                            Импортируем <span style={{
                                fontWeight: 'bold',
                                // badge
                                backgroundColor: 'green',
                                color: 'white',
                                padding: '2px 4px',
                                borderRadius: 4,
                                fontSize: 12,
                            }}>{foods.length}</span> продуктов
                        </div>
                    }
                    {
                        wrongLines.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <div style={{ color: 'red', fontSize: 12 }}>
                            Пропустим строк: <span style={{
                                        fontWeight: 'bold',
                                        // badge
                                        backgroundColor: 'red',
                                        color: 'white',
                                        padding: '2px 4px',
                                        borderRadius: 4,
                                        fontSize: 12,
                                    }}>{wrongLines.length}</span>
                                </div>
                                <SimpleButton size='small' type='transparent' click={
                                    () => {
                                        showMode == 'all' ? setShowMode('wrong') : setShowMode('all')
                                    }
                                }>{showMode == 'all' ? 'Показать ошибки' : 'Показать все'}</SimpleButton>
                            </div>
                        )
                    }
                </div>
                {/* <div>
                    <div>Назначение колонок в файле:</div>
                    <div style={{ maxHeight: 100, overflowY: 'auto' }}>
                        {
                            columns.map((c) => (
                                <div key={c.index}>
                                    <div>{c.content.map((column, index) => <div key={index}>index: {column}</div>)}</div>
                                    <Select options={Columns} change={() => {}} typeId={c.typeId} disable={false} />
                                </div>
                            ))
                        }
                    </div>

                    <Select options={Columns} change={() => {}} typeId={0} disable={false} />

                </div>
                <div>
                    <div>предпросмотр файла:</div>
                    <div style={{ maxHeight: 100, overflowY: 'auto' }}>
                        {linesAndColumns.slice(0, 50).map((line, index) => (
                            <div key={index}>{line.join('; ')}</div>
                        ))}
                    </div>
                </div> */}
            </div>
            <DuetButtons
                first={{
                    type: 'success',
                    content: 'Импортировать',
                    action: runImport,
                    disabled: foods.length === 0,
                }}
                second={{
                    type: 'transparent',
                    content: 'Отменить',
                    action: onCancel,
                }}
                disabled={false}
            />
        </>
    )
}


const BaseView: React.FC<{
    onCsvImport: (linesAndColumns: string[][]) => void
    onImport: (foods: Food[]) => void
    onCancel: () => void
}> = ({ onCsvImport, onImport, onCancel }) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [file, setFile] = useState<File | null>(null)
    const [importPassword, setImportPassword] = useState('')
    const [busy, setBusy] = useState(false)
    // какая кодировка файла windows-1251 или utf-8
    const [code, setCode] = useState<'windows-1251' | 'utf-8'>('utf-8')
    const [lines, setLines] = useState<string[]>([])
    const [separator, setSeparator] = useState<string>('')
    useEffect(() => {
        if (file == null || !file.name.endsWith('.csv')) {
            setLines([])
            return
        }
        readCsvFileToUtf8(code, file).then(([lines, separator]) => {
            setLines(lines)
            setSeparator(separator)
        })
    }, [file, code])

    const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0]
        setFile(f ?? null)
    }

    const runImport = async () => {
        if (file == null) {
            window.alert('Выберите файл')
            return
        }
        setBusy(true)
        try {
            if (file.name.endsWith('.csv')) {
                const linesAndColumns = lines.map(splitCsvLine.bind(null, separator))
                onCsvImport(removeEmptyColumns(removeEmptyLines(linesAndColumns)))
            } else {
                // console.log('import file', file, importPassword)
                const foods = await importFoodsFromDietFile(file, importPassword || undefined)
                onImport(foods)
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err)
            window.alert(msg)
        } finally {
            setBusy(false)
            if (inputRef.current) {
                inputRef.current.value = ''
            }
        }
    }


    return (
        <>
            <div style={{ padding: '2px 0 16px 0', fontSize: 14 }}>
                <h1>Импорт продуктов из файла</h1>
                {
                    file == null || !file.name.endsWith('.csv') ? (
                        <>
                            <p style={{ marginBottom: 8 }}>Форматы:</p>
                            <ul style={{ paddingLeft: 16, marginTop: 0, paddingTop: 0 }}>
                                <li><strong>.diet.gz</strong> - файл экспорта сервиса</li>
                                <li><strong>.csv</strong> - файлы с продуктами, колонки:
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, margin: 4, padding: 8 }}>
                                        {['название', 'теги', 'ФА', 'белок', 'жиры', 'углеводы', 'Ккал'].join('; ')}
                                    </div>
                        на 100г</li>
                            </ul>
                        </>
                    ) : null
                }
                <div style={{ width: '100%', height: 40, position: 'relative' }}>
                    <input
                        style={{ width: '100%', height: '100%', position: 'absolute',
                            top: 0, left: 0, padding: 0, margin: 0, opacity: 0, backgroundColor: 'white' }}
                        ref={inputRef}
                        type="file"
                        accept="*"
                        onChange={onPickFile}
                        title="Выберите файл"
                    />
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        zIndex: 100, pointerEvents: 'none', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <SimpleButton type='transparent' click={() => {}}>{
                            file == null ? 'Выбрать файл' : 'Сменить файл'
                        }</SimpleButton>
                    </div>
                </div>

                {file != null ? <p style={{ fontSize: 14, marginTop: 4 }}>Выбран: {file.name}</p> : null}
                {
                    file?.name.endsWith('.csv') ? (
                        <>
                            <p style={{ fontSize: 14, marginBottom: 4 }}>Кодировка файла:</p>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <SimpleButton type={code === 'windows-1251' ? 'light-success' : 'transparent'}
                                    click={() => setCode('windows-1251')}>
                                    Windows-1251
                                </SimpleButton>
                                <SimpleButton type={code === 'utf-8' ? 'light-success' : 'transparent'}
                                    click={() => setCode('utf-8')}>
                                    UTF-8
                                </SimpleButton>
                            </div>
                            <div style={{ marginTop: 12 }}>
                                <div>предпросмотр файла:</div>
                                <div style={{ maxHeight: 100, overflowY: 'auto' }}>
                                    <div style={{ marginTop: 4 }}>
                                        {lines.map((line, index) => (
                                            <div key={index}>{line}</div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : null
                }
                {/* <p style={{ marginTop: 12 }}>Пароль (если файл зашифрован при экспорте)</p>
                    <input
                        type="password"
                        value={importPassword}
                        onChange={(e) => setImportPassword(e.target.value)}
                        placeholder="оставьте пустым для незашифрованного файла"
                        autoComplete="off"
                    /> */}
            </div>
            <DuetButtons
                first={{
                    type: 'success',
                    content: busy ? 'Импорт…' :
                        file?.name.endsWith('.csv')
                            ? 'Настройка импорта'
                            : 'Импортировать',
                    action: () => void runImport(),
                    disabled: busy || file == null,
                }}
                second={{
                    type: 'transparent',
                    content: 'Отменить',
                    action: onCancel,
                }}
                disabled={busy}
            />
        </>
    )
}


function removeEmptyLines(linesAndColumns: string[][]) {
    return linesAndColumns.filter((line) => line.some((column) => column.trim() !== ''))
}

function removeEmptyColumns(linesAndColumns: string[][]) {
    const columns = linesAndColumns[0].map((column, index) => false)
    for (const line of linesAndColumns) {
        for (let index = 0; index < line.length; index++) {
            if (columns[index]) {
                continue
            }
            if (line[index].trim() !== '') {
                columns[index] = true
            }
        }
    }
    return linesAndColumns.map((line) => line.filter((column, index) => columns[index]))
}

function detectColumnType(headers: string[][], columns: OptionTypeWithPossibleValues[]): { columnIndex: number, typeId: number }[] {
    if (headers.length === 0) {
        return []
    }
    const fileHeaders = headers[0].map((_, index) => {
        return {
            columnIndex: index,
            typeId: 0,
            content: headers
                .flatMap(one => onlyLettersAndNumbers(one[index] ?? '').toLowerCase().trim().split(' ')
                    .flatMap(word => word.trim())
                    .filter(one => one.length > 0),
                ),
        }
    })

    let isEnd = false
    while (!isEnd) {
        isEnd = true
        for (const one of fileHeaders) {
            if (one.typeId !== 0) {
                continue
            }
            const leftColumns = columns.filter(one => !fileHeaders.some(two => two.typeId === one.typeId))
            for (const column of leftColumns) {
                // определяем может это эта колонка
                const isMatch = one.content.some(content => column.possibleValues.includes(content))
                if (isMatch) {
                    one.typeId = column.typeId
                    isEnd = false
                    break
                }
            }
        }
    }

    return fileHeaders

}

// удаляем все символы кроме букв (русских и английских)
function onlyLettersAndNumbers(str: string) {
    return str.replaceAll(/[^а-яА-Яa-zA-Z]/g, ' ')
}